/**
 * Note: lot's of hard-coded things in this chart, because it's the only one.
 * Please adjust, if it turns out we need it more generically.
 */ import * as stylex from "@stylexjs/stylex";
import { Delaunay } from "d3-delaunay";
import { format } from "d3-format";
import * as d3_sankey from "d3-sankey";
import * as d3_shape from "d3-shape";
import { option } from "fp-ts";
import { flatten } from "fp-ts/lib/ReadonlyArray";
import React, { useCallback } from "react";
import { defineMessage, useIntl } from "react-intl";
import ColorLegend from "../charts/lib/ColorLegend";
import { useContentRect, useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints, colors } from "../materials/consts.stylex";
import { layout } from "../materials/layout.stylex";
import { typography } from "../materials/typography.stylex";
import { An, O, pipe, R } from "../prelude";
const formatCurrency = (intl, x)=>{
    const amount = format(".0f")(x);
    return intl.formatMessage(defineMessage({
        id: "USD {amount} million",
        defaultMessage: "USD {amount} million"
    }), {
        amount: amount
    });
};
const NODE_PADDING = 8;
const styles = {
    columnTitle: {
        kDwRjp: "x6ii1vp",
        $$css: true
    }
};
const VoronoiInteractionLayer_ = ({ voronoi, onMouseOverCell, onMouseLeaveCell, onTouchMoveCell, onTouchEndCell, debug })=>{
    return <>
      {Array(voronoi.delaunay.points.length / 2).fill(null).map((_, i)=>{
        return <path stroke={debug ? "rgba(0, 0, 0, 0.25)" : "transparent"} strokeWidth={1} fill="transparent" key={i} d={voronoi.renderCell(i)} data-cellindex={i} onMouseOver={onMouseOverCell} onTouchStart={onTouchMoveCell} onTouchMove={onTouchMoveCell} onTouchEnd={onTouchEndCell} onMouseLeave={onMouseLeaveCell}/>;
    })}
    </>;
};
const VoronoiInteractionLayer = React.memo(VoronoiInteractionLayer_);
const useSankeyVoronoi = ({ width, height, nodes })=>{
    return React.useMemo(()=>{
        const nodeVertices = flatten(nodes.map((node)=>{
            return [
                [
                    node.x0,
                    node.y0
                ],
                [
                    node.x1,
                    node.y0
                ],
                [
                    node.x0,
                    node.y1
                ],
                [
                    node.x1,
                    node.y1
                ]
            ];
        }));
        const delaunay = Delaunay.from(nodeVertices);
        return delaunay.voronoi([
            0,
            0,
            width,
            height
        ]);
    }, [
        nodes,
        width,
        height
    ]);
};
const Sankey = React.memo(Sankey_);
function Sankey_({ data, height, width, colorPalettes, nodeGroups }) {
    const screenSDown = !useMedia(breakpoints.s);
    const dimensions = React.useMemo(()=>{
        const margin = {
            top: 30,
            right: 1,
            bottom: 38,
            left: 1
        };
        const outerHeight = height + margin.top + margin.bottom;
        const innerHeight = height;
        const outerWidth = width;
        const innerWidth = width - margin.left - margin.right;
        return {
            margin,
            outerHeight,
            innerHeight,
            outerWidth,
            innerWidth
        };
    }, [
        height,
        width
    ]);
    const { nodes, links } = React.useMemo(()=>{
        const layout = d3_sankey.sankey().nodeId((x)=>x.id).nodeWidth(screenSDown ? 4 : 16).nodePadding(NODE_PADDING).size([
            dimensions.innerWidth,
            dimensions.innerHeight
        ]);
        const dataWithLayout = layout(data);
        justifyVerticalPositionsMut(dataWithLayout.nodes);
        return dataWithLayout;
    }, [
        screenSDown,
        data,
        dimensions.innerWidth,
        dimensions.innerHeight
    ]);
    return <SankeyRenderer nodes={nodes} links={links} dimensions={dimensions} width={width} height={height} nodeGroups={nodeGroups} colorPalettes={colorPalettes}/>;
}
export function SankeyAuto(props) {
    const [ref, contentRect] = useContentRect();
    return <div ref={ref} {...stylex.props(layout.fullWidth)}>
      {contentRect.width > 0 && <Sankey {...props} width={contentRect.width}/>}
    </div>;
}
const SankeyRenderer_ = React.forwardRef(({ nodes, links, dimensions, width, height, nodeGroups, colorPalettes }, ref)=>{
    const intl = useIntl();
    const screenSDown = !useMedia(breakpoints.s);
    const { margin, outerHeight, innerHeight, outerWidth, innerWidth } = dimensions;
    const [selectedState, setSelected] = React.useState();
    const voronoi = useSankeyVoronoi({
        width,
        height,
        nodes
    });
    const handleMouseOverVoronoiCell = (ev)=>{
        const node = ev.currentTarget;
        if (!node.dataset.cellindex) {
            return;
        }
        const nodeI = Math.floor(Number(node.dataset.cellindex) / 4);
        if (nodeI !== undefined) {
            setSelected(nodes[nodeI]);
        }
    };
    const handleTouchMoveVoronoiCell = handleMouseOverVoronoiCell;
    const handleMouseLeaveVoronoiCell = useCallback(()=>setSelected(undefined), [
        setSelected
    ]);
    const handleTouchEndVoronoiCell = handleMouseLeaveVoronoiCell;
    const selected = option.fromNullable(selectedState);
    const linkIdToNodes = React.useMemo(()=>{
        const res = {};
        for (const link of links){
            res[link.linkId] = res[link.linkId] || new Set();
            res[link.linkId].add(link.source.id);
            res[link.linkId].add(link.target.id);
        }
        return res;
    }, [
        links
    ]);
    const isLinkHighlighted = useCallback((link)=>{
        const nodeId = option.toUndefined(selected);
        if (!nodeId) {
            return false;
        }
        return linkIdToNodes[link.linkId].has(nodeId.id);
    }, [
        selected,
        linkIdToNodes
    ]);
    return <div>
        <svg height={outerHeight} width={outerWidth}>
          <g key={"inner"} transform={`translate(${margin.left}, ${margin.top})`}>
            {nodes.map((node, i)=>{
        const textAnchorLeft = (node.x0 || 0) < innerWidth / 3;
        const width = (node.x1 || 0) - (node.x0 || 0);
        const height = (node.y1 || 0) - (node.y0 || 0);
        const textPadding = screenSDown ? 2 : 8;
        const isHighlighted = pipe(selected, O.exists((x)=>x.id === node.id));
        const nodeGroup = nodeGroups.find((g1)=>g1.depth === node.depth);
        const palette = colorPalettes.find((p)=>p.id === nodeGroup?.category)?.palette || colorPalettes[0].palette;
        return <React.Fragment key={i}>
                  <rect x={node.x0} y={node.y0} width={width} height={height} fill={isHighlighted ? palette[6] : palette[4]}/>
                  <text {...stylex.props(typography.meta)} x={textAnchorLeft ? node.x1 : node.x0} y={(node.y0 || 0) + height / 2} textAnchor={textAnchorLeft ? "start" : "end"} dominantBaseline={"middle"} fill={isHighlighted ? M.blackText : M.lightText}>
                    <tspan x={textAnchorLeft ? node.x1 : node.x0} dx={textAnchorLeft ? `${textPadding}px` : `-${textPadding}px`} dy={"-0.4em"}>
                      {node.id}
                    </tspan>
                    {node.value != null && <tspan x={textAnchorLeft ? node.x1 : node.x0} dx={textAnchorLeft ? `${textPadding}px` : `-${textPadding}px`} dy={"1.2em"} fill={isHighlighted ? M.blackText : M.grayscalePalette[5]}>
                        {formatCurrency(intl, node.value)}
                      </tspan>}
                  </text>
                </React.Fragment>;
    })}
            {links.map((link, i)=>{
        const isHighlighted = isLinkHighlighted(link);
        return <path key={i} d={mkLinkPath(link)} fill={"transparent"} stroke={isHighlighted ? colorPalettes[colorPalettes.length - 1].palette[7] : "#000"} strokeWidth={link.width} opacity={isHighlighted ? 0.25 : 0.05}/>;
    })}
            {nodeGroups.map((nodeGroup)=>{
        return <g key={nodeGroup.nodes}>
                  <text {...stylex.props(typography.table, styles.columnTitle)} x={innerWidth / (nodeGroups.length - 1) * nodeGroup.depth} textAnchor={nodeGroup.depth === 0 ? "start" : nodeGroup.depth === nodeGroups.length - 1 ? "end" : "middle"} y={0} dy={"-1em"}>
                    {nodeGroup.label}
                  </text>
                </g>;
    })}
            <rect ref={ref} x={0} y={0} width={innerWidth} height={innerHeight} fill="transparent"/>
            <VoronoiInteractionLayer voronoi={voronoi} onMouseOverCell={handleMouseOverVoronoiCell} onTouchMoveCell={handleTouchMoveVoronoiCell} onTouchEndCell={handleTouchEndVoronoiCell} onMouseLeaveCell={handleMouseLeaveVoronoiCell}/>
          </g>
        </svg>
        <ColorLegend maxWidth={innerWidth} inline values={colorPalettes.map((p)=>({
            color: p.palette[4],
            label: p.label
        }))}/>
      </div>;
});
const SankeyRenderer = React.memo(SankeyRenderer_);
// -----------------------------------------------------------------------------
const mkLinkPath = d3_shape.linkHorizontal().source((d)=>[
        d.source.x1,
        d.y0
    ]).target((d)=>[
        d.target.x0,
        d.y1
    ]);
/**
 * This function **mutates** an array of nodes to adjust the y-positions of both
 * nodes and links. The goal is to justify all nodes vertically so that the
 * first node in a stack is at yMin and the last one is at yMax.
 *
 * This is needed because there are different numbers of items in each stack,
 * which means that, with a constant padding applies, the stacks will not be
 * the same height when they should.
 */ function justifyVerticalPositionsMut(nodes) {
    const stacks = pipe(nodes, An.groupBy((x)=>x.depth));
    const maxStackItems = pipe(stacks, R.reduce(0, (max, xs)=>xs.length > max ? xs.length : max));
    pipe(stacks, R.map((xs)=>{
        if (xs.length < maxStackItems) {
            const padding = (maxStackItems - 1) * NODE_PADDING / (xs.length - 1);
            let y0 = 0;
            xs.forEach((node)=>{
                const height = node.y1 - node.y0;
                node.y0 = y0;
                node.y1 = y0 + height;
                let y0link = y0;
                let y1link = y0;
                for (const link of node.sourceLinks){
                    link.y0 = y0link + link.width / 2;
                    y0link += link.width;
                }
                for (const link of node.targetLinks){
                    link.y1 = y1link + link.width / 2;
                    y1link += link.width;
                }
                y0 = node.y1 + padding;
            });
        }
    }));
}
